import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Textarea,
  Text,
  VStack,
  HStack,
  Box,
  Radio,
  RadioGroup,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { Flag } from 'lucide-react';
import { useCreateReport } from '@/api';
import type { ReportContentType, ReportReason } from '@/types/social';

interface ReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentType: ReportContentType;
  contentId: number;
  contentDescription?: string; // e.g., "John Doe's post" or "Jane's comment"
  onReported?: () => void;
}

const REPORT_REASONS: Array<{ value: ReportReason; label: string; description: string }> = [
  {
    value: 'spam',
    label: 'Spam',
    description: 'Unwanted commercial content or repetitive posts',
  },
  {
    value: 'harassment',
    label: 'Harassment',
    description: 'Bullying, threats, or targeted attacks',
  },
  {
    value: 'hate_speech',
    label: 'Hate Speech',
    description: 'Content that attacks people based on protected characteristics',
  },
  {
    value: 'violence',
    label: 'Violence',
    description: 'Graphic violence or threats of violence',
  },
  {
    value: 'inappropriate',
    label: 'Inappropriate Content',
    description: 'Sexually explicit or offensive material',
  },
  {
    value: 'misinformation',
    label: 'Misinformation',
    description: 'False or misleading information',
  },
  {
    value: 'impersonation',
    label: 'Impersonation',
    description: 'Pretending to be someone else',
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Another reason not listed above',
  },
];

export function ReportModal({
  isOpen,
  onClose,
  contentType,
  contentId,
  contentDescription,
  onReported,
}: ReportModalProps) {
  const [selectedReason, setSelectedReason] = useState<ReportReason>('spam');
  const [description, setDescription] = useState('');
  const { mutateAsync: createReport, isPending } = useCreateReport();
  const toast = useToast();

  const handleSubmit = async () => {
    try {
      await createReport({
        content_type: contentType,
        content_id: contentId,
        reason: selectedReason,
        description: description.trim() || undefined,
      });

      toast({
        title: 'Report submitted',
        description: 'Thank you for helping keep our community safe. We\'ll review your report shortly.',
        status: 'success',
        duration: 4000,
      });

      // Reset form
      setSelectedReason('spam');
      setDescription('');
      onClose();
      onReported?.();
    } catch (error: any) {
      toast({
        title: 'Failed to submit report',
        description: error?.data?.detail || 'Please try again',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleClose = () => {
    setSelectedReason('spam');
    setDescription('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="orange.100" borderRadius="var(--r-chip)" p={2}>
              <Flag size={20} color="var(--chakra-colors-orange-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Report {contentDescription || 'Content'}
              </Text>
              <Text fontSize="xs" color="var(--card-text-2)" fontWeight="normal">
                Help keep our community safe
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={6}>
            <Text fontSize="sm" color="var(--card-text-2)">
              Please select the reason for your report. Your report is anonymous and helps us maintain a safe community.
            </Text>

            <VStack align="stretch" spacing={4}>
              <Text fontSize="md" fontWeight="semibold">
                Reason for report
              </Text>
              <RadioGroup value={selectedReason} onChange={(value) => setSelectedReason(value as ReportReason)}>
                <Stack spacing={3}>
                  {REPORT_REASONS.map((reason) => (
                    <Radio
                      key={reason.value}
                      value={reason.value}
                      sx={{
                        '.chakra-radio__control[data-checked]': {
                          bg: 'orange.500',
                          borderColor: 'orange.500',
                        },
                      }}
                    >
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium">
                          {reason.label}
                        </Text>
                        <Text fontSize="xs" color="var(--card-text-2)">
                          {reason.description}
                        </Text>
                      </VStack>
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </VStack>

            <VStack align="stretch" spacing={2}>
              <Text fontSize="md" fontWeight="semibold">
                Additional details (optional)
              </Text>
              <Textarea
                placeholder="Provide any additional information that might help us understand the issue..."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={1000}
                rows={4}
              />
              <Text fontSize="xs" color="var(--card-text-2)" textAlign="right">
                {description.length}/1000 characters
              </Text>
            </VStack>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose} isDisabled={isPending}>
            Cancel
          </Button>
          <Button bg="orange.500" color="white" _hover={{ bg: 'orange.600' }} onClick={handleSubmit} isLoading={isPending}>
            Submit Report
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
